/**
 * The two sentences about templates that have to say the same thing everywhere
 * they appear, and the one label that has to count the same way everywhere.
 *
 * They are constants rather than text typed into each screen because both are
 * promises about behaviour rather than decoration. The copy-not-link line is
 * shown twice — on the dialog before she applies a template and in the
 * confirmation after — and the spec requires both, so two divergent wordings
 * would be two different promises. The deletion line is the only thing standing
 * between "delete this template" and a coach who thinks she is about to remove
 * three clients' training.
 */

/**
 * Mechanism and consequence in one line: what applying a template does, and
 * what it therefore will not do later.
 *
 * The second half is the part that matters, because "copy" alone is a word
 * people read as an implementation detail rather than as a promise about
 * tomorrow (`plan-templates`, "Applying a template makes a copy, not a link").
 */
export const COPY_NOT_LINK =
  'This is a copy: later changes to the template will not reach this plan, and changes you ' +
  'make to this plan will not reach the template.';

/** Said in the delete confirmation, because it is the fear the question raises. */
export const DELETE_LEAVES_PLANS =
  'Plans you have already started from this template are not affected. Their weeks stay exactly ' +
  'as they are, and so does what each client sees.';

/**
 * "3 weeks", "1 week", "No weeks" — how many weeks a template holds.
 *
 * Counted from the weeks themselves rather than from a stored number, so this
 * is the only place the wording is decided, and a one-week template is
 * distinguishable from a four-week block at a glance before either is applied.
 */
export function weekCountLabel(weeks: number): string {
  if (weeks === 0) return 'No weeks';
  if (weeks === 1) return '1 week';
  return `${String(weeks)} weeks`;
}
